import { API_CALL, LERE_DISMISS_ALL_STICKY_ABANDONED_CART, LERE_REMOVE_ABANDONED_CART, LERE_REMOVE_HIGH_INTENT, LERE_SAVE_ABANDONED_CART, LERE_SAVE_HOT_LEADS } from 'actions/actionConstants'
import {
  FETCH_CALENDAR_OFFER_RECOMMENDATIONS,
  FETCH_LERE_HIGH_INTENT,
  FETCH_LERE_LOCATION_SEEDED_RECOMMENDATIONS,
  FETCH_LERE_NEARBY_OFFERS,
  FETCH_LERE_OFFER_STATS,
  FETCH_LERE_PERSONALISED_ALTERNATIVES,
  FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME,
  FETCH_LERE_PERSONALISED_PREFERENCES,
  FETCH_LERE_RECOMMENDED_DESTINATIONS,
  FETCH_LERE_REGION_RECOMMENDATIONS,
  FETCH_LERE_TOP_DEALS,
  FETCH_LERE_TOUR_RECOMMENDATIONS,
  FETCH_OFFER_BASED_LERE_RECOMMENDATIONS,
} from 'actions/apiActionConstants'
import * as RecommendationService from 'api/recommendations'
import config from 'constants/config'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import { ALL_GRAPH_RECOMMENDATION_OFFER_TYPES } from 'constants/recommendations'
import { lereDlpParameters, lereLocationSeededCacheKey } from 'lib/customer/recommendationUtils'
import { timeNowInSecond } from 'lib/datetime/time'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { getAllAbandonedCarts, getRecentlyViewedOffers } from 'selectors/recomendations/highIntentSelectors'
import { getAllHotLeadOffers } from 'selectors/recomendations/hotLeadSelectors'
import { getOfferStatsKey } from 'selectors/recomendations/offerStatsSelectors'
import { AppAction } from './ActionTypes'

/**
 *
 * @param offerId offer id
 * @param checkIn Check in date in ISO format
 * @param checkOut Check out date in ISO format
 * @returns
 */
export function fetchOfferBasedRecommendations(offerId: string, allowMultiOffersSameProperty: boolean = false, checkIn?: string, checkOut?: string, occupancy?: string): AppAction {
  return async(dispatch, getState) => {
    // if we have one of these 2 params, we need to make sure both, otherwise skip the request
    if (checkIn || checkOut) {
      if (!(checkIn && checkOut)) {
        return
      }
    }

    const state = getState()
    const cacheKey = offerId

    const filterKey = (!!checkIn && !!checkOut && !!occupancy) ? [checkIn, checkOut, occupancy, allowMultiOffersSameProperty].join(';') : allowMultiOffersSameProperty.toString()

    const recommendationsState = state.recommendations.recommendations[cacheKey]

    if (recommendationsState?.filterKey === filterKey) {
      return
    }

    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_BASED_LERE_RECOMMENDATIONS,
      request: async() => {
        const result = await RecommendationService.getRecommendations(offerId, state.geo.currentRegionCode, canViewLuxPlusBenefits, allowMultiOffersSameProperty, checkIn, checkOut, occupancy)
        const { offers } = result

        const recommendationOfferIds = offers.map((r) => r.offerId)

        return recommendationOfferIds
      },
      cacheKey,
      filterKey,
    })
  }
}

export function fetchCalendarRecommendations(offerId: string, filters: {
  checkIn?: string;
  availableMonth?: string;
  rooms?: Array<App.Occupants>;
  duration?: number;
}): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)

    dispatch({
      type: API_CALL,
      api: FETCH_CALENDAR_OFFER_RECOMMENDATIONS,
      request: async() => {
        const params = {
          checkIn: filters.checkIn,
          availableMonth: filters.availableMonth,
          rooms: filters.rooms,
          duration: filters.duration,
        }

        const { offers } = await RecommendationService.getCalendarRecommendations(offerId, state.geo.currentRegionCode, canViewLuxPlusBenefits, params)
        const recommendationOfferIds = offers.map((r) => r.offerId)

        return {
          offerIds: recommendationOfferIds,
        }
      },
      offerId,
    })
  }
}

interface FetchLocationSeededRecommendationsParams {
  destinationId: string
  polygonMode: RecommendationService.PolygonMode
  nearDestinations: boolean
  useCacheIfExists?: boolean
  checkIn?: string
  checkOut?: string
  duration?: number
  useCentroid?: boolean
  offerTypes?: Array<string>
}

export function fetchLocationSeededRecommendations({
  destinationId,
  polygonMode,
  nearDestinations,
  useCacheIfExists = false,
  checkIn,
  checkOut,
  duration,
  useCentroid = true,
  offerTypes,
}: FetchLocationSeededRecommendationsParams): AppAction {
  return async(dispatch, getState) => {
    const destinationKey = lereLocationSeededCacheKey({
      destinationId,
      polygonMode,
      nearDestinations,
      checkIn,
      checkOut,
      duration,
      offerTypes,
    })

    if (useCacheIfExists) {
      const existingRecommendations = getState().recommendations.locationSeededRecommendations[destinationKey]
      if (existingRecommendations && ['loading', 'done'].includes(existingRecommendations.state)) {
        return
      }
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_LOCATION_SEEDED_RECOMMENDATIONS,
      request: async() => {
        let checkInParam: string | undefined = undefined
        let checkOutParam: string | undefined = undefined
        let durationParam: number | undefined = undefined
        if (checkIn && checkOut) {
          checkInParam = checkIn
          checkOutParam = checkOut
        }
        if (checkIn && duration) {
          checkInParam = checkIn
          durationParam = duration
        }
        const { offers } = await RecommendationService.getLocationSeededRecommendations({
          destinationId,
          polygonMode,
          nearDestination: nearDestinations,
          checkInDate: checkInParam,
          checkOutDate: checkOutParam,
          duration: durationParam,
          useCentroid,
          offerTypes,
        })

        const recommendationOfferIds = offers.map((r) => r.offerId)
        return recommendationOfferIds
      },
      destinationKey,
    })
  }
}

// #region DLP - Destination Landing Page

export function fetchDlpMainRecommendations(destinationId: string): AppAction {
  return fetchLocationSeededRecommendations({
    destinationId,
    polygonMode: lereDlpParameters.mainList.polygonMode,
    nearDestinations: lereDlpParameters.mainList.nearDestinations,
  })
}

export function fetchDlpYmalRecommendations(destinationId: string): AppAction {
  return fetchLocationSeededRecommendations({
    destinationId,
    polygonMode: lereDlpParameters.ymal.polygonMode,
    nearDestinations: lereDlpParameters.ymal.nearDestinations,
    offerTypes: ALL_GRAPH_RECOMMENDATION_OFFER_TYPES,
  })
}

// #endregion

export function fetchPeopleLikeMeRecommendations(): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const currentState = state.recommendations.personalisedPeopleLikeMe.state

    if (currentState === 'loading') { return }

    const leUserId = state.auth.account.memberId
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const region = state.geo.currentRegionCode

    // Work around for conditional dispatch
    let request = () => new Promise((resolve) => resolve({ offers: [], lereVersion: '' }))
    if (leUserId) {
      request = () => RecommendationService.getPeopleLikeMeRecommendations(leUserId, region, canViewLuxPlusBenefits)
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME,
      request,
    })
  }
}

export function fetchTopDeals(region: string): AppAction {
  return async(dispatch, getState) => {
    const state = getState()

    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_TOP_DEALS,
      request: async() => {
        const { offers, lereVersion } = await RecommendationService.getTopDeals(region, canViewLuxPlusBenefits)

        const leOfferIds = offers.filter(o => [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL]
          .includes(o.offer_type))
          .map(o => o.offer_id)

        return { offerIds: leOfferIds, lereVersion }
      },
    })
  }
}

export function fetchHighIntentOffers(): AppAction {
  return async(dispatch, getState) => {
    if (!config.LERE_HIGH_INTENT_CAROUSEL_ENABLED) { return }

    const state = getState()
    const domainUserId = state.auth.domainUserId
    const leUserId = state.auth.account.memberId
    const highIntent = state.recommendations.highIntent
    const region = state.geo.currentRegionCode

    if (!(domainUserId || leUserId) || highIntent.state === 'done') { return }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_HIGH_INTENT,
      request: () => RecommendationService.getHighIntentOffers(region, leUserId, domainUserId),
    })
  }
}

export function addAbandonedCart(offerId: string, offerType: App.OfferType, total: number): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const userId = getCurrentUserId(state)
    const domainUserId = state.auth.domainUserId
    if (!!userId && state.checkout.cart.items.length > 0) {
      const cart: App.AbandonedCartRecommendation = {
        ...state.checkout.cart,
        isDismissed: false,
        offerId,
        offerType,
        creationTime: timeNowInSecond(),
        category: 'sticky_abandoned_carts',
        total,
      }

      RecommendationService.saveCartInLereServiceWithDebounce(cart, domainUserId, userId)

      dispatch({
        type: LERE_SAVE_ABANDONED_CART,
        cart,
      })
    }
  }
}

/**
 * Should be called after checkout
 */
export function removeAbandonedCartsAndDismissOthers(offerIds: Array<string>): AppAction {
  return async(dispatch, getState) => {
    if (!offerIds.length) { return }

    dispatch({
      type: LERE_REMOVE_ABANDONED_CART,
      offerIds,
    })
    dispatch({
      type: LERE_DISMISS_ALL_STICKY_ABANDONED_CART,
    })
    const state = getState()
    const userId = getCurrentUserId(state)
    const domainUserId = state.auth.domainUserId
    if (userId || domainUserId) {
      // this need to be awaited to make sure the AC is removed first
      // before updating the others, else there will be a race condition
      await RecommendationService.removeAbandonedCarts(offerIds, domainUserId, userId)

      const carts = getAllAbandonedCarts(state)
      if (carts.length) {
        RecommendationService.saveAbanonedCarts(carts, domainUserId, userId)
      }
    }
  }
}

export function saveHotLead(hotLead: App.HotLeadOffer): AppAction {
  return (dispatch, getState) => {
    // we always save the state locally
    // only call remote save if user logged in
    dispatch({
      type: LERE_SAVE_HOT_LEADS,
      hotLead,
    })

    const state = getState()
    const userId = getCurrentUserId(state)
    const domainUserId = state.auth.domainUserId

    if (userId || domainUserId) {
      // don't need to await for the response
      RecommendationService.saveHotLeadsWithDebounce([hotLead], domainUserId, userId)
    }
  }
}

export function removeHighIntentOffers(offerIds: Array<string>, highIntentCategories: Array<App.HighIntentCategory>): AppAction {
  return async(dispatch, getState) => {
    if (!offerIds.length) { return }

    dispatch({
      type: LERE_REMOVE_HIGH_INTENT,
      offerIds,
      highIntentCategories,
    })

    const state = getState()
    const userId = getCurrentUserId(state)
    const domainUserId = state.auth.domainUserId
    if (userId || domainUserId) {
      // don't need to await for the response

      if (highIntentCategories.includes('recently_viewed')) {
        RecommendationService.removeRecentlyViewedOffers(offerIds, domainUserId, userId)
      }
      // Hot Leads is the subset of Recent Viewed, will remove both
      if (highIntentCategories.includes('sticky_hot_leads')) {
        RecommendationService.removeHotLeads(offerIds, domainUserId, userId)
        RecommendationService.removeRecentlyViewedOffers(offerIds, domainUserId, userId)
      }
      // Abandoned cart is the subset of Hot Leads and Recent Viewed, will remove all
      if (highIntentCategories.includes('sticky_abandoned_carts')) {
        RecommendationService.removeAbandonedCarts(offerIds, domainUserId, userId)
        RecommendationService.removeHotLeads(offerIds, domainUserId, userId)
        RecommendationService.removeRecentlyViewedOffers(offerIds, domainUserId, userId)
      }
    }
  }
}

export function saveAllHotLeadsAndRecentlyViewed(): AppAction {
  return async(_, getState) => {
    const state = getState()
    const userId = getCurrentUserId(state)
    const domainUserId = state.auth.domainUserId
    if (!userId && !domainUserId) { return }

    const hotLeads = getAllHotLeadOffers(state)
    if (hotLeads.length) {
      RecommendationService.saveHotLeadsWithDebounce(hotLeads, domainUserId, userId)
    }
    const recentlyViewed = getRecentlyViewedOffers(state)
    if (recentlyViewed.length) {
      const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
      RecommendationService.saveRecentlyViewedOffersWithDebounce(recentlyViewed, state.geo.currentRegionCode, canViewLuxPlusBenefits, domainUserId, userId)
    }
  }
}

export function fetchPersonalisedAlternativeOffers() : AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const currentState = state.recommendations.personalisedAlternatives.state

    if (currentState === 'loading') { return }

    const domainUserId = state.auth.domainUserId
    const leUserId = state.auth.account.memberId
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const region = state.geo.currentRegionCode

    // Work around for conditional dispatch
    let request = () => new Promise((resolve) => resolve({ offers: [], lereVersion: '' }))
    if (domainUserId || leUserId) {
      request = () => RecommendationService.getAlternativesRecommendations(region, canViewLuxPlusBenefits, leUserId, domainUserId)
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_ALTERNATIVES,
      request,
    })
  }
}

export function fetchPersonalisedPreferenceOffers(): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const leUserId = state.auth.account.memberId
    const currentState = state.recommendations.personalisedPreferences.state
    const region = state.geo.currentRegionCode

    if (!leUserId || currentState === 'loading') { return }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_PREFERENCES,
      request: () => RecommendationService.getPreferenceRecommendations(region, leUserId),
    })
  }
}

export function fetchOfferStats(offerId: string, region: string): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const offerKey = getOfferStatsKey(offerId, region)
    const offerStats = state.recommendations.offerStats[offerKey]

    // skip API call if already fetching or fetched
    if (!!offerStats && (offerStats.state === 'done' || offerStats.state === 'loading')) { return }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_OFFER_STATS,
      offerKey,
      request: () => RecommendationService.getOfferStats(offerId, region),
    })
  }
}

export function getLereNearbyOffersKey(placeId: string, region: string, occupancy?: Array<App.Occupants>, checkIn?: string, checkOut?: string): string {
  if (occupancy && checkIn && checkOut) {
    // occupancy only get used when we have dates
    return `${placeId}-${region}-${RecommendationService.occupancyToStr(occupancy)}-${checkIn}-${checkOut}`
  }
  return `${placeId}-${region}`
}

export function fetchLereNearbyOffers(placeId: string, occupancy?: Array<App.Occupants>, checkIn?: string, checkOut?: string): AppAction {
  return async(dispatch, getState) => {
    const state: App.State = getState()
    const region = state.geo.currentRegionCode

    const key = getLereNearbyOffersKey(placeId, region, occupancy, checkIn, checkOut)
    const fetchState = state.recommendations.nearbyOffers[key]?.state
    if (fetchState && fetchState !== 'error') { return }

    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_NEARBY_OFFERS,
      key,
      request: () => RecommendationService.getNearbyRecommendations(placeId, region, canViewLuxPlusBenefits, occupancy, checkIn, checkOut),
    })
  }
}

export function getTourKey(
  offerId: string,
  region: string,
  departureYear?: number,
  departureMonth?: number,
) {
  if (!!departureYear && departureMonth !== undefined) {
    return `${offerId}-${region}-${departureYear}-${departureMonth + 1}`
  }
  return `${offerId}-${region}`
}

export function fetchRecommendedTours(
  offerId: string,
  departureYear?: number,
  departureMonth?: number,
  offerType?: Array<App.Tours.V2ProductType>,
): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()
    const region = state.geo.currentRegionCode

    const key = getTourKey(offerId, region, departureYear, departureMonth)
    const tourState = state.recommendations.tour[key]

    if (tourState?.state == 'done' || tourState?.state == 'loading') { return }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_TOUR_RECOMMENDATIONS,
      request: () => RecommendationService.getRecommendedTours(offerId, region, departureYear, departureMonth, offerType),
      key,
    })
  }
}

export function fetchRegionRecommendations(): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const region = state.geo.currentRegionCode

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_REGION_RECOMMENDATIONS,
      key: 'regionRecommendations',
      request: () => RecommendationService.getRegionRecommendations(region),
    })
  }
}

export function fetchRecommendedDestinations(region: string, domainUserId?: string, leUserId?: string): AppAction {
  return async(dispatch) => {
    dispatch({
      type: API_CALL,
      api: FETCH_LERE_RECOMMENDED_DESTINATIONS,
      key: 'recommendedDestinations',
      request: () => RecommendationService.getRecommendedDestinations(region, domainUserId, leUserId),
    })
  }
}
